import Button from '@rio-cloud/rio-uikit/Button';
import Checkbox from '@rio-cloud/rio-uikit/Checkbox';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import EmptyState from '@rio-cloud/rio-uikit/EmptyState';
import SimpleTooltip from '@rio-cloud/rio-uikit/SimpleTooltip';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import TableToolbar, { TableToolbarColumn } from '@rio-cloud/rio-uikit/TableToolbar';
import debounce from 'lodash/debounce';
import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useGetVehiclesQuery } from '#api/resources.slice.ts';
import { useGetFleetGroupsQuery } from '#api/tagsApi.slice.ts';
import { useGetVehicleAvailabilityQuery } from '#api/vehicleAvailability.slice.ts';
import { FleetGroupFilter } from '#components/shared/FleetGroupFilter.tsx';
import { SearchInput } from '#components/shared/SearchInput.tsx';
import type { TourStop } from '#domain/TourStop.ts';
import { getVehicleDisplayName } from '#domain/Vehicle.ts';
import type { VehicleAvailability, VehicleAvailabilityTour } from '#domain/VehicleAvailability.ts';
import {
    getConflictingTours,
    getProposedTourInterval,
    getTimelineBarPosition,
    getTimelineGuides,
    getTimelineRange,
    getTimelineTourLayout,
    intervalsOverlap,
    type TimelineInterval,
    type TimelineRange,
} from '#features/tourCreate/utils/vehicleAvailability.utils.ts';

type VehicleAvailabilityTimelineProps = {
    stops: TourStop[];
    selectedVehicleId: string;
    onSelectVehicle: (vehicleId: string) => void;
    excludedTourId?: string;
    disabled?: boolean;
    showProposedTour?: boolean;
    skip?: boolean;
    vehicleSettingsAction?: ReactNode;
};

const VEHICLE_COLUMN_WIDTH = 150;
const MINIMUM_DAY_WIDTH = 120;
const BAR_HEIGHT = 26;
const BAR_GAP = 8;
const VEHICLE_ROW_HEIGHT = BAR_HEIGHT + BAR_GAP * 2;

const TimelineGuides = ({ range }: { range: TimelineRange }) => (
    <div className='position-absolute top-0 bottom-0 left-0 right-0 pointer-events-none' aria-hidden='true'>
        {getTimelineGuides(range).map(({ time, hour, left }) => (
            <span
                key={time.toMillis()}
                className={`position-absolute top-0 bottom-0 border border-left-only ${hour === 0 ? 'border-color-light' : 'border-color-lighter'}`}
                style={{ left }}
                data-testid='timeline-guide'
                data-hour={hour}
            />
        ))}
    </div>
);

const toIso = (dateTime: TimelineRange['start']) => {
    const iso = dateTime.toUTC().toISO();
    if (!iso) {
        throw new Error('Cannot create a vehicle availability query from an invalid date');
    }
    return iso;
};

const formatRange = (locale: string, tour: VehicleAvailabilityTour) => {
    const start = new Date(tour.start);
    const end = new Date(tour.end);
    const dateFormatter = new Intl.DateTimeFormat(locale, { dateStyle: 'short' });
    const timeFormatter = new Intl.DateTimeFormat(locale, { timeStyle: 'short' });
    return `${dateFormatter.format(start)}, ${timeFormatter.format(start)} – ${dateFormatter.format(end)}, ${timeFormatter.format(end)}`;
};

const TimelineBar = ({
    interval,
    range,
    label,
    title,
    className,
    top,
    zIndex = 0,
}: {
    interval: TimelineInterval;
    range: TimelineRange;
    label: string;
    title: string;
    className: string;
    top: number;
    zIndex?: number;
}) => (
    <span
        className={`position-absolute display-flex align-items-center rounded padding-x-10 text-size-14 text-medium ellipsis-1 overflow-hidden ${className}`}
        style={{
            ...getTimelineBarPosition(interval, range),
            top,
            zIndex,
            height: BAR_HEIGHT,
            minWidth: 3,
            whiteSpace: 'nowrap',
        }}
        title={title}
    >
        {label}
    </span>
);

const VehicleLane = ({
    vehicle,
    range,
    proposedTour,
    selected,
    disabled,
    conflictingTourIds,
    proposedLabel,
    conflictWarning,
    onSelect,
    settingsAction,
}: {
    vehicle: VehicleAvailability;
    range: TimelineRange;
    proposedTour?: TimelineInterval;
    selected: boolean;
    disabled: boolean;
    conflictingTourIds: Set<string>;
    proposedLabel: string;
    conflictWarning?: string;
    onSelect: () => void;
    settingsAction?: ReactNode;
}) => {
    const conflictId = useId();
    const suitabilityClass = conflictingTourIds.size > 0 ? 'alert-warning' : 'bg-white';
    const { entries } = useMemo(() => getTimelineTourLayout(vehicle.tours), [vehicle.tours]);
    const overlapsProposed =
        selected && proposedTour && entries.some(entry => intervalsOverlap(entry.interval, proposedTour));

    return (
        <div
            className={`alert display-flex width-100pct margin-0 padding-0 rounded-none border border-top-only border-width-1 border-color-light text-left ${suitabilityClass} ${selected ? 'bg-highlight-lightest' : ''} ${disabled ? '' : 'cursor-pointer'}`}
        >
            <div
                className={`alert ${suitabilityClass} position-sticky flex-0 display-flex align-items-center gap-5 margin-0 padding-x-10 padding-y-5 rounded-none border border-right-only border-width-1 border-color-light ${selected ? 'bg-highlight-lightest text-color-primary' : ''}`}
                style={{ width: VEHICLE_COLUMN_WIDTH, left: 0, zIndex: 3 }}
            >
                <button
                    type='button'
                    disabled={disabled}
                    onClick={onSelect}
                    aria-pressed={selected}
                    aria-label={getVehicleDisplayName(vehicle)}
                    aria-describedby={conflictWarning ? conflictId : undefined}
                    className='ellipsis-1 overflow-hidden text-nowrap'
                    style={{
                        border: 0,
                        background: 'transparent',
                        padding: 0,
                        color: 'inherit',
                        textAlign: 'left',
                        flex: 1,
                    }}
                    title={[getVehicleDisplayName(vehicle), vehicle.searchName].filter(Boolean).join(' · ')}
                >
                    <strong className='display-block ellipsis-1 overflow-hidden'>
                        {getVehicleDisplayName(vehicle)}
                    </strong>
                </button>
                {selected && settingsAction}
                {conflictWarning && (
                    <SimpleTooltip content={conflictWarning}>
                        <span
                            id={conflictId}
                            role='img'
                            aria-label={conflictWarning}
                            className='uikit-icon icon-warning-sign text-color-warning flex-0 margin-left-5'
                            data-testid='vehicle-conflict-warning'
                        />
                    </SimpleTooltip>
                )}
            </div>
            <button
                type='button'
                disabled={disabled}
                onClick={onSelect}
                tabIndex={-1}
                aria-label={`${getVehicleDisplayName(vehicle)} — ${proposedLabel}`}
                className='position-relative flex-1-1-0 overflow-hidden'
                style={{
                    height: VEHICLE_ROW_HEIGHT,
                    border: 0,
                    padding: 0,
                    background: 'transparent',
                    textAlign: 'left',
                }}
            >
                <TimelineGuides range={range} />
                {entries.map(({ tour, interval }) => (
                    <TimelineBar
                        key={tour.id}
                        interval={interval}
                        range={range}
                        label={tour.name}
                        title={tour.name}
                        top={BAR_GAP}
                        zIndex={conflictingTourIds.has(tour.id) ? 2 : 0}
                        className={`${
                            conflictingTourIds.has(tour.id)
                                ? 'bg-warning text-color-dark'
                                : 'bg-lighter text-color-dark'
                        } ${conflictingTourIds.has(tour.id) ? 'opacity-80' : entries.some(other => other.tour.id !== tour.id && intervalsOverlap(other.interval, interval)) ? 'opacity-50' : ''}`}
                    />
                ))}
                {selected && proposedTour && (
                    <TimelineBar
                        interval={proposedTour}
                        range={range}
                        label={proposedLabel}
                        title={proposedLabel}
                        top={BAR_GAP}
                        zIndex={1}
                        className={`bg-primary text-color-white ${overlapsProposed ? 'opacity-50' : ''}`}
                    />
                )}
            </button>
        </div>
    );
};

export const VehicleAvailabilityTimeline = ({
    stops,
    selectedVehicleId,
    onSelectVehicle,
    excludedTourId,
    disabled = false,
    showProposedTour = true,
    skip = false,
    vehicleSettingsAction,
}: VehicleAvailabilityTimelineProps) => {
    const intl = useIntl();
    const proposedTour = useMemo(() => getProposedTourInterval(stops), [stops]);
    const [showOccupied, setShowOccupied] = useState(false);
    const [groupFilter, setGroupFilter] = useState<string[]>([]);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const searchId = useId();
    const debouncedSearch = useMemo(() => debounce(setSearch, 300), []);
    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);
    const [expanded, setExpanded] = useState(false);
    const expandButtonRef = useRef<HTMLButtonElement>(null);
    const groupsQuery = useGetFleetGroupsQuery(undefined, { skip });
    const resourcesQuery = useGetVehiclesQuery(undefined, { skip });
    const filterUnavailable = groupsQuery.isError || resourcesQuery.isError;
    useEffect(() => {
        if (skip) {
            debouncedSearch.cancel();
            setSearchInput('');
            setSearch('');
            setGroupFilter([]);
            setShowOccupied(false);
            setExpanded(false);
        }
    }, [skip, debouncedSearch]);
    const range = useMemo(() => getTimelineRange(proposedTour), [proposedTour]);
    const query = useMemo(() => ({ from: toIso(range.start), to: toIso(range.end) }), [range]);
    const {
        currentData: data = [],
        isFetching,
        isError,
        refetch,
    } = useGetVehicleAvailabilityQuery(query, {
        skip,
        refetchOnMountOrArgChange: true,
    });
    const vehicles = useMemo(
        () => [...data].sort((left, right) => getVehicleDisplayName(left).localeCompare(getVehicleDisplayName(right))),
        [data]
    );
    const matchingVehicleIds = new Set(
        resourcesQuery.data
            ?.filter(vehicle => vehicle.fleetGroupIds?.some(id => groupFilter.includes(id)))
            .map(vehicle => vehicle.id)
    );
    const normalizedSearch = search.trim().toLowerCase();
    const hasActiveFilters = normalizedSearch !== '' || (groupFilter.length > 0 && !filterUnavailable);
    const matchingVehicles = vehicles.filter(
        vehicle =>
            (groupFilter.length === 0 || filterUnavailable || matchingVehicleIds.has(vehicle.vehicleId)) &&
            (normalizedSearch === '' ||
                vehicle.plate?.toLowerCase().includes(normalizedSearch) ||
                vehicle.searchName.toLowerCase().includes(normalizedSearch))
    );
    const vehicleConflictsById = useMemo(
        () =>
            new Map(
                vehicles.map(vehicle => [
                    vehicle.vehicleId,
                    getConflictingTours(vehicle.tours, proposedTour, excludedTourId),
                ])
            ),
        [vehicles, proposedTour, excludedTourId]
    );
    const occupiedCount = matchingVehicles.filter(
        vehicle => (vehicleConflictsById.get(vehicle.vehicleId)?.length ?? 0) > 0
    ).length;
    const visibleVehicles = matchingVehicles.filter(
        vehicle =>
            showOccupied ||
            vehicle.vehicleId === selectedVehicleId ||
            (vehicleConflictsById.get(vehicle.vehicleId)?.length ?? 0) === 0
    );
    const conflicts = vehicleConflictsById.get(selectedVehicleId) ?? [];
    const title = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.vehicle-availability.title',
    });
    const proposedLabel = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.vehicle-availability.proposed-tour',
    });
    const conflictMessage = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.vehicle-availability.conflict',
    });
    const conflictDetails = conflicts.map(tour => `${tour.name}: ${formatRange(intl.locale, tour)}`).join('; ');
    const searchLabel = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.vehicle-availability.search',
    });

    const renderAvailabilityCheckbox = (enlarged: boolean) => (
        <Checkbox
            id={`${searchId}-occupied-${enlarged ? 'expanded' : 'compact'}`}
            checked={showOccupied}
            onChange={() => setShowOccupied(current => !current)}
        >
            <FormattedMessage
                id='intl-msg:ordercom-cloud-frontend.tours.create-dialog.vehicle-availability.show-occupied'
                values={{ count: occupiedCount }}
            />
        </Checkbox>
    );

    const renderTimeline = (enlarged: boolean) => (
        <section
            className={`display-flex flex-column flex-1-1 gap-10 min-height-0 min-width-0 ${enlarged ? '' : 'height-100pct'}`}
            aria-label={title}
            aria-busy={isFetching}
            style={enlarged ? { height: Math.max(250, Math.min(750, window.innerHeight - 200)) } : undefined}
        >
            <div className='display-flex flex-wrap justify-content-between align-items-end gap-10 flex-0'>
                <div className='display-flex flex-wrap align-items-center gap-20 min-width-0'>
                    {!enlarged && <h4 className='margin-0'>{title}</h4>}
                    <div className='width-150'>
                        <SearchInput
                            id={`${searchId}-${enlarged ? 'expanded' : 'compact'}`}
                            ariaLabel={searchLabel}
                            placeholder={searchLabel}
                            value={searchInput}
                            onSearch={value => {
                                setSearchInput(value);
                                debouncedSearch(value);
                            }}
                        />
                    </div>
                    <FleetGroupFilter
                        compact
                        fleetGroups={groupsQuery.data ?? []}
                        value={groupFilter}
                        onChange={setGroupFilter}
                        isLoading={groupsQuery.isFetching || resourcesQuery.isFetching}
                        disabled={filterUnavailable}
                        className='tour-fleet-group-filter flex-0 width-150'
                    />
                    {enlarged && proposedTour && renderAvailabilityCheckbox(enlarged)}
                </div>
                {!enlarged && (
                    <Button
                        ref={expandButtonRef}
                        className='margin-left-auto'
                        bsStyle='muted'
                        bsSize='sm'
                        iconName='icon-fullscreen'
                        onClick={() => setExpanded(true)}
                    >
                        {intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.vehicle-availability.expand',
                        })}
                    </Button>
                )}
            </div>
            {!enlarged && proposedTour && (
                <TableToolbar disableResponsiveCollapse className='flex-0 margin-bottom-0'>
                    <TableToolbarColumn className='margin-bottom-0'>
                        {renderAvailabilityCheckbox(enlarged)}
                    </TableToolbarColumn>
                </TableToolbar>
            )}
            {isFetching ? (
                <div className='display-grid place-items-center min-height-100'>
                    <Spinner />
                </div>
            ) : isError ? (
                <EmptyState
                    condensed
                    outerClassName='border-none margin-0'
                    headline={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.vehicle-availability.error',
                    })}
                    buttons={[
                        {
                            bsStyle: Button.PRIMARY,
                            onClick: () => refetch(),
                            text: intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.vehicle-availability.retry',
                            }),
                        },
                    ]}
                />
            ) : visibleVehicles.length === 0 ? (
                <EmptyState
                    condensed
                    outerClassName='border-none margin-0'
                    headline={intl.formatMessage({
                        id:
                            matchingVehicles.length > 0
                                ? 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.vehicle-availability.none-available'
                                : hasActiveFilters
                                  ? 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.vehicle-availability.no-results'
                                  : 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.vehicle-availability.empty',
                    })}
                />
            ) : (
                <div className='border rounded bg-white shadow-subtle overflow-auto flex-0-1 min-height-0'>
                    <div
                        className='border border-bottom-only border-color-light'
                        style={{ minWidth: VEHICLE_COLUMN_WIDTH + range.days.length * MINIMUM_DAY_WIDTH }}
                    >
                        <div className='display-flex bg-lightest position-sticky top-0' style={{ zIndex: 4 }}>
                            <div
                                className='position-sticky flex-0 padding-15 text-medium border border-right-only bg-lightest'
                                style={{ width: VEHICLE_COLUMN_WIDTH, left: 0, zIndex: 5 }}
                            >
                                {intl.formatMessage({
                                    id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.tourTruck',
                                })}
                            </div>
                            {range.days.map(day => (
                                <div
                                    key={day.toISODate()}
                                    className='flex-1-1-0 padding-top-10 border border-right-only text-center text-nowrap'
                                    style={{ minWidth: MINIMUM_DAY_WIDTH }}
                                >
                                    <div className='text-medium padding-x-5 margin-bottom-10'>
                                        {day
                                            .setLocale(intl.locale)
                                            .toLocaleString({ weekday: 'short', day: '2-digit', month: '2-digit' })}
                                    </div>
                                    <div
                                        className='display-grid grid-cols-4 text-size-12 text-color-dark border border-top-only'
                                        aria-hidden='true'
                                    >
                                        {['00', '06', '12', '18'].map(hour => (
                                            <span key={hour} className='padding-y-5 border border-right-only'>
                                                {hour}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                        {visibleVehicles.map(vehicle => {
                            const vehicleConflicts = vehicleConflictsById.get(vehicle.vehicleId) ?? [];
                            return (
                                <VehicleLane
                                    key={vehicle.vehicleId}
                                    vehicle={vehicle}
                                    range={range}
                                    proposedTour={showProposedTour ? proposedTour : undefined}
                                    selected={vehicle.vehicleId === selectedVehicleId}
                                    disabled={disabled}
                                    conflictingTourIds={new Set(vehicleConflicts.map(tour => tour.id))}
                                    proposedLabel={proposedLabel}
                                    conflictWarning={
                                        vehicle.vehicleId === selectedVehicleId && conflicts.length > 0
                                            ? `${conflictMessage} ${conflictDetails}`
                                            : undefined
                                    }
                                    onSelect={() => onSelectVehicle(vehicle.vehicleId)}
                                    settingsAction={vehicleSettingsAction}
                                />
                            );
                        })}
                    </div>
                </div>
            )}
        </section>
    );

    return (
        <>
            {renderTimeline(false)}
            {expanded && !skip && (
                <Dialog
                    show
                    title={title}
                    bsSize='lg'
                    onClose={() => {
                        setExpanded(false);
                        expandButtonRef.current?.focus();
                    }}
                    body={renderTimeline(true)}
                />
            )}
        </>
    );
};
